/**
 * Utilities for page range calculations and conversions in MetaDoc
 */

/**
 * Calculate new cursor position after page deletion
 * Returns the page number that should be focused after deletion
 */
export const calculateNewCursorPosition = (
  cursorPosition: number,
  deletedPageRanges: number[][],
  newTotalPages: number,
): number => {
  if (newTotalPages === 0) {
    return 1;
  }

  // count how many pages before or at the cursor were deleted
  let deletedPagesBeforeOrAtCursor = 0;
  let cursorWasDeleted = false;

  for (const [start, end] of deletedPageRanges) {
    if (end < cursorPosition) {
      // entire range is before cursor
      deletedPagesBeforeOrAtCursor += end - start + 1;
    } else if (start <= cursorPosition && cursorPosition <= end) {
      // cursor is within this deleted range
      cursorWasDeleted = true;
      deletedPagesBeforeOrAtCursor += cursorPosition - start + 1;
      break;
    }
  }

  if (cursorWasDeleted) {
    // cursor was on a deleted page, move to first page after the deletion range
    // find the first page after the range that contained the cursor
    let firstPageAfterDeletion = cursorPosition;

    for (const [start, end] of deletedPageRanges) {
      if (start <= cursorPosition && cursorPosition <= end) {
        firstPageAfterDeletion = end + 1;
        break;
      }
    }

    // count pages deleted before the first page after deletion
    let deletedBeforeAfter = 0;
    for (const [start, end] of deletedPageRanges) {
      if (end < firstPageAfterDeletion) {
        deletedBeforeAfter += end - start + 1;
      }
    }

    // calculate new position: first page after deletion minus pages deleted before it
    const newPosition = firstPageAfterDeletion - deletedBeforeAfter;

    // ensure we don't go beyond total pages, and if all pages after were deleted, go to last page
    if (newPosition > newTotalPages) {
      return newTotalPages;
    }
    return Math.max(1, newPosition);
  }

  // cursor was not deleted, just adjust for pages deleted before it
  const newPosition = cursorPosition - deletedPagesBeforeOrAtCursor;
  return Math.min(Math.max(1, newPosition), newTotalPages);
};

/**
 * Calculate new cursor position after page movement
 * @param cursorPosition - Current cursor position before movement
 * @param movedPageRanges - Ranges of pages being moved
 * @param targetPosition - Target position where pages are moved to
 * @param totalPages - Total pages (unchanged by movement)
 * @returns New cursor position after movement
 */
export const calculateNewCursorPositionAfterMovement = (
  cursorPosition: number,
  movedPageRanges: number[][],
  targetPosition: number,
  totalPages: number,
): number => {
  if (totalPages === 0) {
    return 1;
  }

  // flatten moved page ranges into a set for easy lookup
  const movedPages = new Set<number>();
  let totalMovedPages = 0;
  let minMovedPage = Infinity;
  let maxMovedPage = -Infinity;

  for (const [start, end] of movedPageRanges) {
    for (let page = start; page <= end; page++) {
      movedPages.add(page);
      totalMovedPages++;
      minMovedPage = Math.min(minMovedPage, page);
      maxMovedPage = Math.max(maxMovedPage, page);
    }
  }

  // case 1: cursor is in moved pages - follow them to new position
  if (movedPages.has(cursorPosition)) {
    // calculate offset of cursor within moved pages
    const sortedMovedPages = Array.from(movedPages).sort((a, b) => a - b);
    const offsetInMovedPages = sortedMovedPages.indexOf(cursorPosition);
    return targetPosition + offsetInMovedPages;
  }

  // case 2: cursor is not in moved pages - adjust for shift
  const movingForward = targetPosition > maxMovedPage;
  const movingBackward = targetPosition < minMovedPage;

  if (movingForward) {
    // pages moved forward (to higher position)
    if (cursorPosition < minMovedPage) {
      // cursor is before moved pages - no change
      return cursorPosition;
    } else if (
      cursorPosition > maxMovedPage &&
      cursorPosition < targetPosition
    ) {
      // cursor is between source and target - shift backward
      return cursorPosition - totalMovedPages;
    } else {
      // cursor is after target position - no change
      return cursorPosition;
    }
  } else if (movingBackward) {
    // pages moved backward (to lower position)
    if (cursorPosition > maxMovedPage) {
      // cursor is after moved pages - no change
      return cursorPosition;
    } else if (
      cursorPosition < minMovedPage &&
      cursorPosition >= targetPosition
    ) {
      // cursor is between target and source - shift forward
      return cursorPosition + totalMovedPages;
    } else {
      // cursor is before target position - no change
      return cursorPosition;
    }
  }

  // fallback: no change
  return cursorPosition;
};

/**
 * Convert a Set of page numbers to an array of contiguous page ranges
 * Example: Set([1, 2, 3, 5, 7, 8]) => [[1, 3], [5, 5], [7, 8]]
 */
export const convertToPageRanges = (pages: Set<number>): number[][] => {
  if (pages.size === 0) {
    return [];
  }

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  const pageRanges: number[][] = [];

  let rangeStart = sortedPages[0];
  let rangeEnd = sortedPages[0];

  for (let i = 1; i < sortedPages.length; i++) {
    if (sortedPages[i] === rangeEnd + 1) {
      rangeEnd = sortedPages[i];
    } else {
      pageRanges.push([rangeStart, rangeEnd]);
      rangeStart = sortedPages[i];
      rangeEnd = sortedPages[i];
    }
  }
  pageRanges.push([rangeStart, rangeEnd]);

  return pageRanges;
};
